import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHead, Badge } from "../../components/ui-bits";
import { useAgentClient, NeedClient } from "../../components/AgentContext";
import { ChatThread } from "../../components/ChatThread";
import Bookkeeping from "../Bookkeeping";
import { toast } from "sonner";
import { EnvelopeSimple, Ticket } from "@phosphor-icons/react";

const inputCls = "border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

export function AgentCompliance() {
  const { sel, selClient } = useAgentClient();
  const [comp, setComp] = useState([]);
  const [n, setN] = useState({ name: "", date: "" });
  const load = () => sel && api.get(`/compliance?client_email=${encodeURIComponent(sel)}`).then((r) => setComp(r.data)).catch(() => setComp([]));
  useEffect(() => { load(); }, [sel]);

  const add = async (e) => {
    e.preventDefault();
    if (!n.name || !n.date) { toast.error("Name & date required"); return; }
    await api.post("/compliance", { client_email: sel, ...n });
    toast.success("Compliance item added"); setN({ name: "", date: "" }); load();
  };
  const cycle = async (item) => {
    const next = item.status === "upcoming" ? "completed" : item.status === "completed" ? "overdue" : "upcoming";
    await api.patch(`/compliance/${item.id}`, { status: next }); load();
  };
  const remind = async () => {
    const { data } = await api.post("/compliance/remind", { client_email: sel });
    toast.success(data.sent ? `Reminder emailed (${data.count} deadline(s))` : "No upcoming deadlines");
  };
  const cols = [["overdue", "Overdue", "#FF3B30"], ["upcoming", "Upcoming", "#F5A623"], ["completed", "Completed", "#00A86B"]];

  return (
    <div>
      <PageHead eyebrow={selClient ? `Compliance Calendar · ${selClient.company}` : "Compliance Calendar"} title="Compliance">
        {selClient && <button data-testid="comp-remind" onClick={remind} className="border border-black/20 hover:bg-black/5 px-3 py-2 rounded-sm text-sm inline-flex items-center gap-2"><EnvelopeSimple size={15} /> Email reminder</button>}
      </PageHead>
      <NeedClient>
        <form onSubmit={add} className="card p-5 mb-5 flex flex-wrap items-end gap-2">
          <input data-testid="comp-name" placeholder="Filing name (e.g. GST GSTR-3B)" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} className={`${inputCls} flex-1 min-w-[200px]`} />
          <input data-testid="comp-date" type="date" value={n.date} onChange={(e) => setN({ ...n, date: e.target.value })} className={inputCls} />
          <button data-testid="comp-add" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm">Add deadline</button>
        </form>
        <div className="grid md:grid-cols-3 gap-4">
          {cols.map(([k, l, c]) => (
            <div key={k} className="card p-5" data-testid={`comp-col-${k}`}>
              <p className="label mb-3" style={{ color: c }}>{l} ({comp.filter((x) => x.status === k).length})</p>
              <div className="space-y-2">
                {comp.filter((x) => x.status === k).map((item, i) => (
                  <div key={item.id} data-testid={`comp-${k}-${i}`} className="flex items-center justify-between gap-2 text-sm border-l-2 pl-3 py-1" style={{ borderColor: c }}>
                    <span>{item.name}<br /><span className="text-xs text-[#888] tnum">{item.date}</span></span>
                    <button data-testid={`comp-toggle-${k}-${i}`} onClick={() => cycle(item)} title="Click to change status"><Badge status={item.status} /></button>
                  </div>
                ))}
                {comp.filter((x) => x.status === k).length === 0 && <p className="text-xs text-[#888]">None</p>}
              </div>
            </div>
          ))}
        </div>
      </NeedClient>
    </div>
  );
}

export function AgentMessages() {
  const { sel, selClient } = useAgentClient();
  return (
    <div>
      <PageHead eyebrow="Talk To Your Client" title="Messages" />
      <NeedClient>
        <p className="text-sm text-[#555] mb-4">Direct line to <b>{selClient?.company}</b> ({selClient?.name}).</p>
        <div className="max-w-2xl"><ChatThread key={sel} clientEmail={sel} height="h-[calc(100vh-320px)]" /></div>
      </NeedClient>
    </div>
  );
}

export function AgentBookkeeping() {
  const { sel } = useAgentClient();
  return <NeedClient><Bookkeeping key={sel} clientEmail={sel} /></NeedClient>;
}

export function AgentTickets() {
  const [tickets, setTickets] = useState([]);
  useEffect(() => { api.get("/agent/tickets").then((r) => setTickets(r.data)).catch(() => {}); }, []);
  const setStatus = async (t, status) => {
    await api.patch(`/tickets/${t.id}/status`, { status });
    toast.success(`Task marked ${status}`);
    setTickets((p) => p.map((x) => (x.id === t.id ? { ...x, status } : x)));
  };
  return (
    <div>
      <PageHead eyebrow="Assigned by Admin" title="Admin Tasks" />
      {tickets.length === 0 ? <p className="card p-8 text-sm text-[#888] text-center">No tasks from admin.</p> : (
        <div className="card divide-y divide-black/10" data-testid="agent-tickets">
          {tickets.map((t, i) => (
            <div key={t.id} data-testid={`agent-ticket-${i}`} className="p-4 flex flex-wrap items-center gap-3">
              <Ticket size={18} className="text-[#002FA7]" />
              <div className="flex-1 min-w-[220px]"><p className="font-medium text-sm">{t.subject}</p>
                <p className="text-xs text-[#888]">{t.category} · from {t.raised_by}{t.description ? ` · ${t.description}` : ""}</p></div>
              <Badge status={t.status === "Resolved" ? "completed" : t.status === "In Progress" ? "upcoming" : "overdue"}>{t.status}</Badge>
              <select data-testid={`agent-ticket-status-${i}`} value={t.status} onChange={(e) => setStatus(t, e.target.value)} className="border border-black/20 rounded-sm px-2 py-1.5 text-sm">
                <option>Open</option><option>In Progress</option><option>Resolved</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
